import { CallType, callTypeFromCode } from '../../models/CallType'
import { Environment } from '../../models/Environment'
import { getCookie } from '../../utils'
import { v4 as uuidv4 } from 'uuid'

export const MAX_AGENCIES = 10
export type FeedType = 'SAVED' | 'NEW'

class FeedConfiguration {
  public id: string
  public name: string
  public agencyIDs: string[]
  public token: string
  public callTypes: CallType[]
  public environment: Environment
  public type: FeedType
  public hideMedical: boolean
  public hideRecent: boolean
  public showFullPSAP: boolean

  public constructor(
    id: string | undefined,
    name: string,
    agencyIDs: string[],
    token: string,
    callTypes: CallType[],
    environment: Environment,
    type: FeedType,
    hideMedical: boolean,
    hideRecent: boolean,
    showFullPSAP: boolean
  ) {
    this.id = id ?? uuidv4()
    this.name = name
    this.agencyIDs = agencyIDs
    this.token = token
    this.callTypes = callTypes
    this.environment = environment
    this.type = type
    this.hideMedical = hideMedical
    this.hideRecent = hideRecent
    this.showFullPSAP = showFullPSAP
  }

  public copy() {
    return new FeedConfiguration(
      this.id,
      this.name,
      this.agencyIDs,
      this.token,
      this.callTypes,
      this.environment,
      this.type,
      this.hideMedical,
      this.hideRecent,
      this.showFullPSAP
    )
  }

  public withEnvironment(environment: Environment) {
    this.environment = environment
    return this
  }

  public withShowFullPSAP(showFullPSAP: boolean) {
    this.showFullPSAP = showFullPSAP
    return this
  }

  public duplicates(configuration: FeedConfiguration, includeName: boolean) {
    return (
      (!includeName || this.name === configuration.name) &&
      this.environment === configuration.environment &&
      this.hideMedical === configuration.hideMedical &&
      this.hideRecent === configuration.hideRecent &&
      this.showFullPSAP === configuration.showFullPSAP &&
      this.token === configuration.token &&
      this.agencyIDs.length === configuration.agencyIDs.length &&
      this.callTypes.length === configuration.callTypes.length &&
      this.agencyIDs.every((agencyID, index) => configuration.agencyIDs[index] === agencyID) &&
      this.callTypes.every((callType, index) => configuration.callTypes[index] === callType)
    )
  }

  public static configurationFromSaved(saved: any) {
    const id = saved['i'] as string
    if (!id || id.length === 0) {
      return null
    }

    const agencyIDs = saved['s']?.split(',')
    if (!agencyIDs || agencyIDs.length === 0) {
      return null
    }

    const name = (saved['n'] ?? '') as string
    const callTypesIDs = (saved['c']?.split(',') ?? []) as string[]
    const sandbox = saved['env'] === 's'
    const nomed = saved['nomed'] === true
    const norec = saved['norec'] === true
    return new FeedConfiguration(
      id,
      name,
      agencyIDs.slice(0, MAX_AGENCIES),
      '',
      callTypesIDs.map(id => callTypeFromCode(id)).filter(callType => callType.id !== 'UNK'),
      sandbox ? 'SANDBOX' : 'PRODUCTION',
      'SAVED',
      nomed,
      norec,
      false
    )
  }

  public toSaved() {
    return {
      i: this.id,
      n: this.name,
      s: this.agencyIDs.join(','),
      c: this.callTypes.map(callType => callType.id).join(','),
      env: this.environment === 'SANDBOX' ? 's' : 'p',
      nomed: this.hideMedical,
      norec: this.hideRecent
    }
  }

  public isValid() {
    return this.agencyIDs.length > 0 || this.token.length > 0
  }

  public link() {
    const agencyIDsParam = this.agencyIDs.length === 0 ? '' : `agencies=${this.agencyIDs.join(',')}`
    const callTypesParam =
      this.callTypes.length === 0 ? '' : `incidenttypes=${this.callTypes.map(callType => callType.id).join(',')}`
    const hideMedicalParam = this.hideMedical ? 'nomed=1' : ''
    const hideRecentParam = this.hideRecent ? 'norec=1' : ''

    const params = [agencyIDsParam, callTypesParam, hideMedicalParam, hideRecentParam].filter(value => value != '')
    const currentURL = new URL(window.location.href)
    const baseURI = currentURL.origin + currentURL.pathname
    return `${baseURI}?${params.join('&')}`
  }

  public static fromV1Cookies(): FeedConfiguration {
    const agencyIDList = getCookie('agencies') ?? ''
    const agencyIDs = agencyIDList.length > 0 ? agencyIDList.split(',') : []
    const callTypesList = getCookie('incident_types') ?? ''
    const callTypesIDs = callTypesList.length > 0 ? callTypesList.split(',') : []
    const callTypes = callTypesIDs
      .map(id => callTypeFromCode(id))
      .filter(callType => callType.category !== 'Unknown' && callType.category !== 'Alert')
    return new FeedConfiguration(undefined, '', agencyIDs, '', callTypes, 'PRODUCTION', 'NEW', false, false, false)
  }

  public static fromURLSearch(params: URLSearchParams): FeedConfiguration {
    const agencyIDList = params.get('agencies') ?? params.get('agencyid') ?? ''
    const agencyIDs = agencyIDList.length > 0 ? agencyIDList.split(',') : []
    const callTypesList = params.get('incidenttypes') ?? ''
    const callTypesIDs = callTypesList.length > 0 ? callTypesList.split(',') : []
    const nomed = params.get('nomed') === '1'
    const norec = params.get('norec') === '1'
    const callTypes = callTypesIDs
      .map(id => callTypeFromCode(id))
      .filter(callType => callType.category !== 'Unknown' && callType.category !== 'Alert')
    return new FeedConfiguration(undefined, '', agencyIDs, '', callTypes, 'PRODUCTION', 'NEW', nomed, norec, false)
  }
}

export default FeedConfiguration
